import React from 'react';
import {
  Stethoscope, MessagesSquare, ShieldCheck, FileCheck2, ScanLine, CreditCard,
  Megaphone, LineChart, UserRound, CalendarClock, Server, Lock, RefreshCw, Globe,
} from 'lucide-react';
import V2Layout from '../components/V2Layout';
import {
  FadeUp, Stagger, StaggerItem, SectionHead, CtaGhost, PageHero, Check, Marquee,
  RuleLabel, BigStat, TextLink, ClosingCta,
} from '../components/V2Shared';
import { PLATFORM_PRODUCTS, SPRAWL_STACK } from '../data/site';

const ICONS = {
  pms: Stethoscope,
  comms: MessagesSquare,
  verify: ShieldCheck,
  claims: FileCheck2,
  imaging: ScanLine,
  payments: CreditCard,
  marketing: Megaphone,
  analytics: LineChart,
  portal: UserRound,
  scheduling: CalendarClock,
};

const CLOUD_POINTS = [
  { icon: Server, title: 'No server in the closet', body: 'Fully cloud-based. No hardware to buy, patch, back up, or replace every five years.' },
  { icon: Lock, title: 'HIPAA-grade by default', body: 'Encrypted in transit and at rest, with audit trails on every record touch.' },
  { icon: RefreshCw, title: 'Always the newest version', body: 'Updates ship continuously. No upgrade weekends, no version migrations, ever.' },
  { icon: Globe, title: 'Run it from anywhere', body: 'The whole practice — schedule, charts, inbox — from any browser, any location.' },
];

const V2Platform = () => (
  <V2Layout title="The Platform — Asha">
    <PageHero
      eyebrow="The platform / 10+ software products"
      title={<>Ten products' worth of software. <span className="text-[#1a46e5]">One operating system.</span></>}
      body="Everything below ships as one product, on one data model, behind one login. Nothing to integrate, nothing to sync, nothing bolted on. This is what makes end-to-end automation possible — and what point solutions can never give you."
    >
      <CtaGhost to="/book-demo">See it live</CtaGhost>
      <TextLink to="/v2/why-asha">Why one platform beats ten tools</TextLink>
    </PageHero>

    <Marquee items={PLATFORM_PRODUCTS.map((p) => p.name)} />

    {/* ---- product index ---- */}
    <section className="px-6 py-20 md:py-24">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="index / 01–10" />
        <Stagger className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-[#dde5f3] bg-[#dde5f3] sm:grid-cols-2 lg:grid-cols-5">
          {PLATFORM_PRODUCTS.map((p) => {
            const Icon = ICONS[p.key];
            return (
              <StaggerItem key={p.key} className="h-full">
                <a href={`#${p.key}`} className="group flex h-full flex-col gap-3 bg-white p-6 transition-colors hover:bg-[#f6f9ff]">
                  <div className="flex items-center justify-between">
                    <Icon className="h-5 w-5 text-[#1a46e5]" />
                    <span className="v2-mono text-[11px] font-medium text-[#a8bcf0]">{p.index}</span>
                  </div>
                  <span className="v2-display text-[17px] font-semibold leading-tight text-[#0a1633] group-hover:text-[#1a46e5]">
                    {p.name}
                  </span>
                  {p.soon && (
                    <span className="v2-mono w-fit rounded-full border border-[#c4d2ec] px-2 py-0.5 text-[9.5px] uppercase tracking-wider text-[#7c89a8]">
                      coming soon
                    </span>
                  )}
                </a>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </section>

    {/* ---- product deep dives: alternating spec blocks ---- */}
    <section className="px-6 pb-24">
      <div className="mx-auto max-w-7xl space-y-20 md:space-y-28">
        {PLATFORM_PRODUCTS.map((p, i) => {
          const Icon = ICONS[p.key];
          const flip = i % 2 === 1;
          return (
            <div key={p.key} id={p.key} className="scroll-mt-32">
              <RuleLabel label={`${p.index} / ${p.name}`} />
              <div className={`mt-10 grid items-center gap-12 lg:grid-cols-2 lg:gap-20 ${flip ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                <FadeUp>
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#eef3ff]">
                      <Icon className="h-5 w-5 text-[#1a46e5]" />
                    </span>
                    {p.soon && (
                      <span className="v2-mono rounded-full border border-[#c4d2ec] px-2.5 py-1 text-[10px] uppercase tracking-wider text-[#7c89a8]">
                        coming soon
                      </span>
                    )}
                  </div>
                  <h2 className="v2-display mt-6 text-[32px] font-semibold leading-[1.08] text-[#0a1633] md:text-[42px]">
                    {p.name}
                  </h2>
                  <p className="mt-4 max-w-xl text-lg leading-relaxed text-[#3f4d6e]">{p.desc}</p>
                  <ul className="mt-7 space-y-3">
                    {p.features.map((f) => (
                      <Check key={f}>{f}</Check>
                    ))}
                  </ul>
                </FadeUp>
                <FadeUp delay={0.12}>
                  <div className="v2-cross relative">
                    <div className="v2-canvas rounded-2xl p-8 md:p-10">
                      <div className="v2-window overflow-hidden rounded-xl">
                        <div className="v2-mono flex items-center justify-between border-b border-[#dde5f3] bg-[#f6f9ff] px-4 py-2.5 text-[10.5px] font-medium uppercase tracking-[0.12em] text-[#7c89a8]">
                          <span>asha — {p.name.toLowerCase()}</span>
                          <span>{p.index} of 10</span>
                        </div>
                        <div className="space-y-2.5 bg-white p-5">
                          {p.features.slice(0, 4).map((f, j) => (
                            <div key={j} className="flex items-center gap-3 rounded-lg border border-[#eef3ff] bg-[#fbfcff] px-3.5 py-2.5">
                              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${j === 0 ? 'v2-pulse bg-[#0f9d76]' : 'bg-[#c4d2ec]'}`} />
                              <span className="truncate text-[13px] font-medium text-[#3f4d6e]">{f}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="v2-mono mt-5 flex items-center justify-between text-[10.5px] font-medium uppercase tracking-[0.14em] text-[#7c89a8]">
                        <span>replaces</span>
                        <span className="text-right text-[#a0aac4] line-through decoration-[#d8614f]/60">{p.replaces}</span>
                      </div>
                    </div>
                  </div>
                </FadeUp>
              </div>
            </div>
          );
        })}
      </div>
    </section>

    {/* ---- one data model panel ---- */}
    <section className="px-6 pb-24 md:pb-32">
      <div className="mx-auto max-w-7xl">
        <div className="v2-panel relative overflow-hidden rounded-[2rem] px-7 py-16 md:px-14 md:py-24">
          <SectionHead
            dark
            center
            eyebrow="the part you can't see"
            title={<>One data model is the whole trick.</>}
            body="When the phone system, the chart, the ledger, and the claim live in one place, an AI agent can carry a workflow from first ring to posted payment without ever losing context. That's the automation threshold ten integrated tools can never reach."
          />
          <FadeUp delay={0.15}>
            <div className="mx-auto mt-14 grid max-w-4xl grid-cols-2 gap-y-10 border-t border-white/15 pt-10 md:grid-cols-4">
              <BigStat dark value="1" label="login" />
              <BigStat dark value="1" label="data model" />
              <BigStat dark value="0" label="integrations to babysit" />
              <BigStat dark value="0" label="servers on site" />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    {/* ---- cloud benefits ---- */}
    <section className="px-6 pb-24 md:pb-28">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="cloud / by default" />
        <div className="mt-12">
          <SectionHead
            eyebrow="Cloud native"
            title={<>The last software migration you'll ever do.</>}
          />
        </div>
        <Stagger className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {CLOUD_POINTS.map((c) => (
            <StaggerItem key={c.title} className="h-full">
              <div className="v2-card h-full rounded-2xl p-7">
                <c.icon className="h-5 w-5 text-[#1a46e5]" />
                <div className="v2-display mt-4 text-[19px] font-semibold leading-tight text-[#0a1633]">{c.title}</div>
                <p className="mt-2.5 text-[14px] leading-relaxed text-[#3f4d6e]">{c.body}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* ---- consolidation receipt ---- */}
    <section className="px-6 pb-24 md:pb-32">
      <div className="mx-auto max-w-4xl">
        <FadeUp>
          <div className="overflow-hidden rounded-2xl border border-[#dde5f3]">
            <div className="v2-mono flex items-center justify-between bg-[#f6f9ff] px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7c89a8]">
              <span>what asha consolidates</span>
              <span>10 categories</span>
            </div>
            {SPRAWL_STACK.map((s, i) => (
              <div key={s.name} className="v2-row flex items-baseline gap-5 px-6 py-3.5">
                <span className="v2-mono w-8 shrink-0 text-[11px] text-[#a8bcf0]">{String(i + 1).padStart(2, '0')}</span>
                <span className="text-[15px] font-semibold text-[#0a1633]">{s.name}</span>
                <span className="v2-mono ml-auto text-right text-[11.5px] text-[#a0aac4]">{s.vendors}</span>
              </div>
            ))}
            <div className="border-t-2 border-[#1a46e5] bg-[#eef3ff] px-6 py-5 text-center">
              <span className="v2-display text-[20px] font-semibold text-[#0a1633]">
                → one platform, one login, one place where work gets done
              </span>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>

    <ClosingCta
      title="Tour all ten products in one walkthrough."
      body="Thirty minutes, your workflows, every module live — from the first phone call to the posted payment."
    />
  </V2Layout>
);

export default V2Platform;
